// T-15 — helpers puros del data-shaping de series de Chart.
//
// Extraídos de `src/components/Chart.tsx` (el componente renderiza highcharts y
// es intratable bajo jsdom). Estas funciones construyen los datos de las series
// finales que alimentan `options.series`. Son puras (dados los inputs devuelven
// el mismo resultado), así que se pueden testear sin renderizar el componente.
//
// `SelectedSeries` se define aquí para que este módulo NO importe `./Chart`
// (que arrastraría highcharts/antd); Chart.tsx lo re-exporta (patrón T-14) para
// no romper sus consumidores (`indicators-model`, `IndicatorTable`, ...).

import { colors } from '../config/config'
import {
  ChartTypesEnum,
  FinalSeries,
  isSingleYearType,
  Series,
  transformSeries,
  WaterfallData,
} from '../config/indicators-model'

export type SelectedSeries = Series & {
  name: string
  dashed: boolean
  excludedTypes: ChartTypesEnum[]
}

/** Suma el valor de todas las series en cada posición de índice, con padding
 *  a 0 para las series más cortas. Base para el modo Percentage (÷ suma × 100). */
export function computeSumSeries(selectedSeries: SelectedSeries[]): number[] {
  return selectedSeries[0].values.map((_value, index) => {
    return selectedSeries
      .map(series => (index < series.values.length ? series.values[index].value : 0))
      .reduce((prev, current) => prev + current, 0)
  })
}

type BuildAllSeriesInput = {
  selectedSeries: SelectedSeries[]
  selectedChartType: ChartTypesEnum
  selectedCategoryIndex: number
  sumSeries: number[]
  disableSeries?: string[]
}

/** Normaliza las series seleccionadas a `FinalSeries` para highcharts:
 *  - tipo "single-year" → se queda solo con la categoría seleccionada;
 *  - Percentage → convierte cada valor a porcentaje sobre la suma del índice;
 *  - sierie con guiones → marca `dashStyle: 'dash'`;
 *  - serie excluida por `disableSeries` → `visible: false`.
 *  Finalmente aplica `transformSeries` (Column1Y / Waterfall). */
export function buildAllSeries({
  selectedSeries,
  selectedChartType,
  selectedCategoryIndex,
  sumSeries,
  disableSeries,
}: BuildAllSeriesInput): FinalSeries[] {
  return transformSeries(
    selectedSeries
      ? selectedSeries.map(item => {
        const selectedValues = isSingleYearType(selectedChartType)
          ? [item.values[selectedCategoryIndex]]
          : selectedChartType === ChartTypesEnum.Percentage
            ? item.values.map((v, index) => ({
              ...v,
              value: (v.value / sumSeries[index]) * 100,
            }))
            : item.values
        return {
          name: item.name,
          ...(item.dashed ? { dashStyle: 'dash' } : {}),
          data: selectedValues.map(point => point.value),
          visible: !disableSeries || !disableSeries.length || !disableSeries.includes(item.key),
        }
      })
      : [],
    selectedChartType,
  )
}

/** Construye las series del Waterfall a partir de `allSeries`: marca la última
 *  como suma (`isSum: true`), le añade el año al nombre y colorea por posición
 *  (primera, última-suma, intermedia) con `colors.alternatives`. */
export function buildWaterfallSeries(
  allSeries: FinalSeries[],
  selectedSeries: SelectedSeries[],
  selectedYear: number,
): WaterfallData[] {
  return allSeries.reduce((prev: WaterfallData[], current, index) => {
    const wfData = current.data[0] as WaterfallData
    const lastElem = index === allSeries.length - 1
    return [
      ...prev,
      {
        ...wfData,
        name: `${selectedSeries[index].name}${lastElem ? ` ${selectedYear}` : ''}`,
        isSum: lastElem,
        color: colors.alternatives[index === 0 ? 0 : lastElem ? 1 : 2],
      },
    ]
  }, [])
}